import opentype, { Font } from 'opentype.js';
import { GraphQLService } from '../GraphQLService/GraphQLService';
import { Mapper } from '../DataStore/DataStore';
import { SocialMediaAsset } from 'models/SocialMediaAsset/SocialMediaAsset';
import { BaseRecordService } from 'utils/BaseRecordService/BaseRecordService';
import { SocialMediaAssetImpl } from 'models/SocialMediaAsset/SocialMediaAssetImpl';
import { SAMSearch } from './SAMSearch';
import { FontStyle, FontWeight } from 'models/Theme/Theme';

const FONT_TIMEOUT = 5000;

export class SocialMediaAssetService extends BaseRecordService<SocialMediaAsset> {
    constructor(graphQL: GraphQLService, mapper: Mapper<SocialMediaAsset>) {
        super(graphQL, mapper);
        // this.queries = new GraphQLQueries();
    }

    find(id: string): Promise<SocialMediaAsset> {
        return this.queryRaw(
            {
                query: `query findAsset($id: ID!){
                     sam {
                         socialMediaAssets(id:$id) {
                            ${SocialMediaAssetImpl.graphQLFields}
                         }
                     }
                 }`,
                variables: { id: id },
            },
            (data: any) => {
                if (
                    data &&
                    data.sam &&
                    data.sam.socialMediaAssets &&
                    data.sam.socialMediaAssets.length
                ) {
                    return data.sam.socialMediaAssets[0];
                } else {
                    return null;
                }
            }
        );
    }

    search(search: SAMSearch): Promise<Array<SocialMediaAsset>> {
        return this.queryManyRaw(
            {
                query: `query searchAssets($search: samSearch!){
                     sam {
                         socialMediaAssets(search:$search) {
                            ${SocialMediaAssetImpl.graphQLFields}
                         }
                     }
                 }`,
                variables: { search: search },
            },
            (data: any) => {
                if (
                    data &&
                    data.sam &&
                    data.sam.socialMediaAssets &&
                    data.sam.socialMediaAssets.length
                ) {
                    return data.sam.socialMediaAssets;
                } else {
                    return [];
                }
            }
        ).then(this.enrichAssets);
    }

    public static isFont(asset: SocialMediaAsset) {
        if (asset.assetType === 'FONT') {
            if (!asset.digitalAsset) {
                return false;
            }
            const mediaType = asset.digitalAsset.mediaMimeType;

            switch (mediaType) {
                case 'application/x-font-otf':
                case 'font/opentype':
                case 'font/truetype':
                    return true;

                default:
                    // console.warn(`Not treating asset '${asset.name}' (${asset.id}) as font, mediaType: ${mediaType}`);
                    return false;
            }
        }
        return false;
    }

    public static isFontFamily(asset: SocialMediaAsset) {
        if (asset.assetType === 'FONT_FAMILY') {
            if (asset.fontFamilyAsset?.fontList?.length > 0) {
                return true;
            }
        }

        return false;
    }

    public static convertRawAssets(rawAssets: Array<SocialMediaAsset>) {
        const assets = [];

        const mimeTypeFromFileName = (url: string) => {
            if (url.endsWith('.ttf')) {
                return 'font/truetype';
            }

            if (url.endsWith('.otf')) {
                return 'font/opentype';
            }

            return null;
        };

        rawAssets.map(asset => {
            // If a font family, break into individual fonts
            if (SocialMediaAssetService.isFontFamily(asset)) {
                asset.fontFamilyAsset.fontList.forEach(font => {
                    if (font.source?.url) {
                        const newAsset = {
                            ...asset,
                            name: font.source.name,
                            assetType: 'FONT',
                        };
                        newAsset.digitalAsset = {
                            mediaUrl: font.source.url,
                            mediaMimeType: mimeTypeFromFileName(font.source.url),
                        };
                        assets.push(newAsset);
                    }
                });
                return;
            }

            // Otherwise, just add the asset
            assets.push(asset);
        });

        return assets;
    }

    private enrichAssets(rawAssets: Array<SocialMediaAsset>): Promise<Array<SocialMediaAsset>> {
        const assets = SocialMediaAssetService.convertRawAssets(rawAssets);

        const promises = assets.map(
            asset =>
                new Promise(async (resolve, reject) => {
                    // check for the mime type too, SVGs and other crap sneak in here.
                    if (SocialMediaAssetService.isFont(asset)) {
                        const fontUrl: string = asset.digitalAsset.mediaUrl;

                        try {
                            const buffer = fetch(fontUrl, {
                                signal: AbortSignal.timeout(FONT_TIMEOUT),
                            }).then(res => res.arrayBuffer());
                            const font = opentype.parse(await buffer);

                            if (font) {
                                const family =
                                    font.names.fontFamily && font.names.fontFamily['en']
                                        ? font.names.fontFamily['en']
                                        : font.names.fullName['en'];
                                const subFamily: string = font.names.fontSubfamily['en'];
                                let style: FontStyle = 'normal';
                                let weight: FontWeight = 'normal';

                                // TODO: support weirdo weights
                                if (subFamily.toLowerCase().indexOf('bold') !== -1) {
                                    weight = 'bold';
                                }

                                if (subFamily.toLowerCase().indexOf('oblique') !== -1) {
                                    style = 'oblique';
                                }

                                if (subFamily.toLowerCase().indexOf('italic') !== -1) {
                                    style = 'italic';
                                }

                                asset.digitalAsset.font = {
                                    samId: asset.id,
                                    name: asset.name,
                                    description: asset.description,
                                    url: asset.digitalAsset.mediaUrl,
                                    family: family,
                                    subFamily: subFamily,
                                    style: style,
                                    weight: weight,
                                };

                                asset.sortKey = family
                                    ? (family + subFamily).toLowerCase()
                                    : asset.name
                                    ? asset.name.toLowerCase()
                                    : '';

                                resolve(asset);
                            } else {
                                // skip enrichment
                                resolve(asset);
                            }
                        } catch (error) {
                            if (error) {
                                // TODO: use name-based fallback instead? Just log it?
                                const msg = `opentype.js error for asset '${asset.name}' (${asset.id}) from URL ${fontUrl}`;
                                console.error(msg, error, asset);
                                resolve(asset);
                            }
                        }
                    } else {
                        resolve(asset);
                    }
                }) as Promise<SocialMediaAsset>
        );

        return Promise.all(promises);
    }
}
